import React, { useEffect } from 'react';
import { Landmark } from 'lucide-react';
import { Card, CardHeader, CardFooter, AddButton, CollapsibleContent, EditModeToggle, EditModeCheckbox, EditModeActionBar } from '../shared';
import { InlineEditField } from '../shared/InlineEditField';
import { UpgradePrompt } from '../modals/UpgradeModal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { useInlineEdit } from '../../hooks/useInlineEdit';
import { useCollapsedWidgets } from '../../hooks/useCollapsedWidgets';
import { useFinancialCalculations } from '../../hooks/useFinancialCalculations';

import { useEditMode } from '../../hooks/useEditMode';
import { formatCurrency } from '../../utils/formatters';
import { BankAccount } from '../../types';

interface BankingWidgetProps {
  onAdd: () => void;
  onUpgradeClick: () => void;
  variant?: 'card' | 'page';
}

export const BankingWidget: React.FC<BankingWidgetProps> = ({
  onAdd,
  onUpgradeClick,
  variant = 'card',
}) => {
  const { darkMode, privacyMode } = useTheme();
  const { sheetId } = useAuth();
  const { updateBankingData, totalBanking, handleDeleteBankAccount, handleBatchDelete } = useFinancialData();
  const { visibleBankingData, hasHiddenBanking, hiddenBankingCount, isAtBankingLimit } = useFinancialCalculations();
  const { editValue, setEditValue, isEditing, startEdit, cancelEdit, completeEdit } = useInlineEdit();
  const { isCollapsed, toggleCollapse } = useCollapsedWidgets();
  const collapsed = isCollapsed('banking');

  const handleDelete = async (index: number) => {
    if (!sheetId) return;
    await handleDeleteBankAccount(index, sheetId);
  };

  const handleBatchDeleteAsync = async (indices: number[]) => {
    if (!sheetId) return;
    await handleBatchDelete('Banking', indices, sheetId);
  };

  const editMode = useEditMode(handleDelete, visibleBankingData.length, handleBatchDeleteAsync);
  const { exitEditMode } = editMode;

  // Exit edit mode when widget collapses
  useEffect(() => {
    if (collapsed) exitEditMode();
  }, [collapsed, exitEditMode]);

  const handleSave = async (index: number, cell: string) => {
    const { value, isValid } = completeEdit();
    if (isValid && sheetId) {
      await updateBankingData(index, value, sheetId);
    }
  };

  const handleAddClick = () => {
    if (isAtBankingLimit) {
      onUpgradeClick();
    } else {
      onAdd();
    }
  };

  const isPage = variant === 'page';

  return (
    <Card variant={variant}>
      <CardHeader
        title="Banking"
        icon={<Landmark size={20} />}
        action={
          <div className="flex items-center gap-2">
            <EditModeToggle
              isEditMode={editMode.isEditMode}
              onToggle={editMode.toggleEditMode}
              itemCount={visibleBankingData.length}

            />
            <div data-tour="add-account-button">
              <AddButton onClick={handleAddClick} />
            </div>
          </div>
        }
        collapsed={isPage ? false : collapsed}
        onToggleCollapse={isPage ? undefined : () => toggleCollapse('banking')}
        variant={variant}
      />

      <CollapsibleContent collapsed={isPage ? false : collapsed} variant={variant}>
        {editMode.isEditMode && (
          <EditModeActionBar
            selectedCount={editMode.selectedIndices.size}
            totalCount={visibleBankingData.length}
            allSelected={editMode.selectedIndices.size === visibleBankingData.length}
            onSelectAll={() => editMode.selectAll(visibleBankingData.length)}
            onDeselectAll={editMode.deselectAll}
            onDeleteSelected={editMode.deleteSelected}
            isDeleting={editMode.isDeleting}
          />
        )}

        <div className="space-y-3 text-sm pb-3">
          {visibleBankingData.map((account: BankAccount, idx: number) => (
            <div key={idx} className="flex justify-between items-center" {...(idx === 0 ? { 'data-tour': 'edit-balance' } : {})}>
              {editMode.isEditMode && (
                <div className="mr-2">
                  <EditModeCheckbox
                    checked={editMode.selectedIndices.has(idx)}
                    onToggle={() => editMode.toggleIndex(idx)}
                  />
                </div>
              )}
              <span className={`${darkMode ? 'text-slate-300' : 'text-slate-600'} flex-1`}>
                {account.name}
              </span>
              <InlineEditField
                value={account.balance}
                isEditing={isEditing('banking', idx, 'balance')}
                editValue={editValue}
                onEditValueChange={setEditValue}
                onStartEdit={() => startEdit('banking', idx, 'balance', account.balance)}
                onSave={() => handleSave(idx, account.cell)}
                onCancel={cancelEdit}
                editable={account.editable}
                variant="positive"
                privacyMode={privacyMode}
              />
            </div>
          ))}

          {hasHiddenBanking && (
            <UpgradePrompt
              count={hiddenBankingCount}
              type="accounts"
              onClick={onUpgradeClick}
            />
          )}
        </div>

      </CollapsibleContent>

      <CardFooter>
        <div className="flex justify-between items-center font-semibold text-sm">
          <span className={`${darkMode ? 'text-white' : 'text-slate-800'} flex-1`}>Total</span>
          <div className={`${darkMode ? 'text-green-400' : 'text-green-600'} text-right w-24 md:w-28 ml-auto`}>
            {formatCurrency(totalBanking, privacyMode)}
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};
